/**
 * SponsorLockup — "Presented by" + separator + sponsor logo.
 *
 * Pulls the active sponsor from config (v1) since the full per-event sponsor
 * model is a follow-up. Director sets `logoUrl` + `name` directly in the
 * layout JSON.
 */

import {
  registerPrimitive,
  type PrimitiveProps,
} from '../../layout'
import { useComponentLiveState } from '../../destination'

interface SponsorLockupConfig {
  label?: string
  name?: string
  logoUrl?: string
  href?: string
}

export function SponsorLockup({ id, config }: PrimitiveProps) {
  const cfg = config as SponsorLockupConfig
  const { state } = useComponentLiveState(id)
  const liveState = state && typeof state === 'object' && !Array.isArray(state)
    ? state
    : null
  const visible = liveState?.visible
  const liveMessage = liveState?.message
  if (visible === false) return null
  if (!cfg.logoUrl && !cfg.name) return null

  const label = typeof liveMessage === 'string'
    ? liveMessage
    : (cfg.label ?? 'Presented by')

  const inner = (
    <>
      <span className="lv-sponsor-badge">{label}</span>
      <span className="lv-sponsor-sep" />
      {cfg.logoUrl ? (
        <img className="lv-sponsor-logo" src={cfg.logoUrl} alt={cfg.name ?? 'Sponsor'} />
      ) : (
        <span style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: 700, fontSize: 16, letterSpacing: '-0.02em' }}>
          {cfg.name}
        </span>
      )}
    </>
  )

  return (
    <div className="lv-rail-sponsor">
      {cfg.href ? (
        <a className="lv-sponsor" href={cfg.href} target="_blank" rel="noreferrer">
          {inner}
        </a>
      ) : (
        <div className="lv-sponsor">{inner}</div>
      )}
    </div>
  )
}

registerPrimitive('SponsorLockup', SponsorLockup)
